import { useEffect, useId, useMemo, useState } from 'react';
import { ArrowRight, Check, Package, Plus, Trash } from 'shared/icons';
import api from '../../../services/api';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import Modal from '../../ui/Modal';
import { Id, MONO_CLASS } from '../../ui/Mono';
import Select from '../../ui/Select';
import Textarea from '../../ui/Textarea';
import { FormatLocationPlain } from '../collector/collectorScan';
import { useActiveLocations } from '../lpnPickers';
import { ExtractListRows } from '../payload';
import {
    CreateServiceOrderItemDraft,
    IsBlankServiceOrderItemDraft,
    IsCompleteServiceOrderItemDraft,
    SERVICE_ORDER_CREATE_STATUSES,
    SERVICE_ORDER_STATUS,
    ServiceOrderItemsFromDrafts,
    ServiceOrderStatusVariant,
} from './serviceOrderModel';

/**
 * CRIAÇÃO AVULSA de OS (`POST /warehouse/service-orders`) — pedido do dono,
 * 11/08: *"Criação avulsa de OS = Podemos criar agora."*
 *
 * ── Esta tela é a SAÍDA DE EMERGÊNCIA, não a jornada ────────────────────────
 * "Alocar carga" é o caminho normal: o Supervisor diz o que vai para onde e o
 * PLANEJADOR monta a jornada do pallet (posição → cabeceira → transporte →
 * posição), decide em quantas OSs isso cai e amarra os handoffs. Aqui não há
 * nada disso: o Supervisor escreve os itens EXATAMENTE como quer que sejam
 * executados, e é isso que o coletor vai mostrar, na ordem em que ele digitou.
 *
 * É deliberado. Quando o planejador não descreve o caso real (o pallet que já
 * está na cabeceira, o remanejo que o Supervisor quer fazer sem passar por
 * lugar nenhum, a OS de auditoria que só tira um LPN do lugar), a alternativa
 * de não ter uma saída é a operação acontecer FORA do sistema. Por isso a tela
 * existe — e por isso ela diz, na cara, que não planeja nada.
 *
 * ── O que NÃO entra aqui, e por quê ─────────────────────────────────────────
 * - **`group_id`**: o wire aceita, mas ele só faz sentido quando existe uma
 *   jornada DIVIDIDA entre equipamentos — e o que amarra o par desde 11/08 é o
 *   `handoff_from_item_id` (M5 r1, regra 4), que esta rota não escreve. Um
 *   campo de grupo aqui produziria OSs "de par" sem espera nenhuma: duas
 *   pessoas achando que estão sincronizadas, e ninguém esperando ninguém.
 * - **produto + quantidade** (item fracionado): terreno do M7. O wire aceita o
 *   par, o coletor ainda executa LPN inteiro.
 * - **rota**: `route_algorithm` não nasce na criação — a escolha é do
 *   comparador, na ficha da OS, depois que ela existe.
 *
 * ── Cadastro manda no comportamento ─────────────────────────────────────────
 * A lista de tipos vem do CADASTRO (tipos ativos), nunca de um enum escrito
 * aqui: o `behavior_key` do tipo escolhido aparece do lado para o Supervisor
 * ver o que está prestes a criar (um tipo com comportamento REPLENISHMENT não
 * vira "movimentação" só porque foi criado à mão).
 */

// Status inicial: FILA DE DECISÃO. É o mesmo padrão do wire e o lado seguro da
// escolha — uma OS que nasce liberada já pode estar na mão de um operador antes
// de o Supervisor reler o que digitou. Quem quer o contrário troca no seletor,
// que está logo ali e diz a consequência.
const DEFAULT_STATUS = SERVICE_ORDER_STATUS.BACKLOG;

const ServiceOrderCreateModal = ({
    label,
    types = [],
    canReadLocations = false,
    canReadEquipment = false,
    onClose,
    onCreated,
    onUseAllocation,
}) => {
    const [typeCode, setTypeCode] = useState('');
    const [status, setStatus] = useState(DEFAULT_STATUS);
    const [equipmentCode, setEquipmentCode] = useState('');
    const [notes, setNotes] = useState('');
    const [rows, setRows] = useState(() => [CreateServiceOrderItemDraft()]);
    const [typeMissing, setTypeMissing] = useState(false);
    const [invalidRowKeys, setInvalidRowKeys] = useState([]);
    const [submitError, setSubmitError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [created, setCreated] = useState(null);
    const [equipmentRows, setEquipmentRows] = useState([]);

    const locationListId = useId();
    const equipmentListId = useId();

    // Sugestões são AJUDA, não requisito (mesma regra da alocação): sem
    // permissão de ler o cadastro a lista some e o Supervisor digita o código —
    // a tela não vira refém de uma permissão que não é a dela.
    const locations = useActiveLocations(canReadLocations);

    useEffect(() => {
        if (!canReadEquipment) {
            setEquipmentRows([]);
            return undefined;
        }
        let alive = true;
        api.getWarehouseEquipment({ active: 'true', limit: 200 })
            .then((data) => {
                if (alive) {
                    setEquipmentRows(ExtractListRows(data, ['equipment']));
                }
            })
            .catch(() => {
                if (alive) {
                    setEquipmentRows([]);
                }
            });
        return () => {
            alive = false;
        };
    }, [canReadEquipment]);

    const locationByCode = useMemo(() => {
        const map = new Map();
        for (const row of locations.rows) {
            map.set(String(row.code || '').toUpperCase(), row);
        }
        return map;
    }, [locations.rows]);

    const selectedType = useMemo(
        () => types.find((type) => String(type.code) === typeCode) ?? null,
        [types, typeCode],
    );

    /**
     * Texto de APOIO cujo rótulo pode ainda não estar no dicionário. Chave sem
     * cadastro volta como a própria chave (ver `useWarehouseLabels`), e uma tela
     * mostrando `wms.service_orders.new_help` é pior que uma tela calada: só
     * entra quando o dicionário responde de verdade.
     */
    const helpText = (key) => {
        const text = label(key);
        return text === key ? null : text;
    };

    // ⚠️ As três frases desta tela ainda NÃO estão no dicionário do módulo
    // (`warehouseLabels.js` é do dono) — estão relatadas para entrar. Enquanto
    // não entram, o bloco correspondente simplesmente não aparece: a tela
    // continua honesta (o botão "Alocar carga" e a descrição dela, que já
    // existem, dizem qual é o caminho automático).
    const newHelp = helpText('wms.service_orders.new_help');
    const itemsHelp = helpText('wms.service_orders.new_items_help');
    const noTypesHelp = helpText('wms.service_orders.new_no_types');

    const changeRow = (rowKey, patch) => {
        const next = rows.map((row) => (row.key === rowKey ? { ...row, ...patch } : row));
        setRows(next);
        // A marca de erro sai quando a linha FICA completa — não a cada tecla.
        // Limpar no primeiro caractere digitado no LPN apagaria o aviso de que
        // falta o endereço, que é o que a linha ainda precisa.
        if (IsCompleteServiceOrderItemDraft(next.find((row) => row.key === rowKey))) {
            setInvalidRowKeys((previous) => previous.filter((key) => key !== rowKey));
        }
    };

    const addRow = () => {
        setRows((previous) => [...previous, CreateServiceOrderItemDraft()]);
    };

    const removeRow = (rowKey) => {
        setRows((previous) => previous.filter((row) => row.key !== rowKey));
        setInvalidRowKeys((previous) => previous.filter((key) => key !== rowKey));
    };

    // Linhas com ALGUMA coisa escrita: uma linha totalmente vazia é a sobra de
    // um "Adicionar item" e sai sem alarde. Meia linha, não — descartá-la em
    // silêncio criaria uma OS diferente da que o Supervisor montou.
    const filledRows = rows.filter((row) => !IsBlankServiceOrderItemDraft(row));
    const completeRows = filledRows.filter(IsCompleteServiceOrderItemDraft);

    /**
     * Validação NO CLIQUE, não no botão desabilitado: um "Salvar" apagado por
     * uma linha pela metade não diz qual linha nem o que falta — o Supervisor
     * fica olhando para um botão morto. O clique marca os campos que faltam.
     *
     * Sem NENHUM item completo, quem pede as pontas são todas as linhas (a
     * vazia inclusive): é ali que o item vai ser escrito.
     */
    const handleSubmit = async (event) => {
        event.preventDefault();
        const incomplete = filledRows.filter((row) => !IsCompleteServiceOrderItemDraft(row));
        setTypeMissing(!typeCode);
        setInvalidRowKeys((completeRows.length === 0 ? rows : incomplete).map((row) => row.key));
        if (!typeCode || incomplete.length > 0 || completeRows.length === 0) {
            return;
        }

        const trimmedEquipment = equipmentCode.trim();
        const trimmedNotes = notes.trim();
        const payload = {
            type_code: typeCode,
            status,
            ...(trimmedEquipment ? { equipment_code: trimmedEquipment } : {}),
            ...(trimmedNotes ? { notes: trimmedNotes } : {}),
            items: ServiceOrderItemsFromDrafts(rows),
        };

        setSaving(true);
        setSubmitError(null);
        try {
            const detail = await api.createWarehouseServiceOrder(payload);
            // RECIBO, como na alocação: a OS nasceu com um código, e é por ele
            // que o Supervisor vai falar dela no rádio. Um aviso passageiro
            // levaria esse código embora antes de alguém ler.
            setCreated(detail);
            onCreated?.();
        } catch (error) {
            setSubmitError(error?.message || label('wms.common.load_error'));
        } finally {
            setSaving(false);
        }
    };

    const receipt = created ? (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-mono-200 bg-mono-50 p-3 dark:border-mono-800 dark:bg-mono-900/40">
                <Check size={18} className="shrink-0 text-success-600" />
                <Id value={created.code} className="text-base font-semibold text-black dark:text-white" />
                <Badge variant={ServiceOrderStatusVariant(created.status)}>
                    {label(`wms.service_order.status.${created.status}`)}
                </Badge>
                <span className="text-sm text-mono-600 dark:text-mono-400">
                    {created.type_label || created.type_code}
                </span>
            </div>

            <ol className="space-y-1">
                {(created.items ?? []).map((item) => (
                    <li
                        key={item.id}
                        className="flex flex-wrap items-center gap-1.5 text-[11px] text-mono-600 dark:text-mono-400"
                    >
                        <Id value={item.seq} className="text-[11px]" />
                        <Id value={item.from_location_code} className="text-[11px]" />
                        <ArrowRight size={11} className="shrink-0" />
                        <Id value={item.to_location_code} className="text-[11px]" />
                        {item.lpn_code ? <Id value={item.lpn_code} className="text-[11px]" /> : null}
                    </li>
                ))}
            </ol>

            <div className="flex justify-end pt-2">
                <Button variant="secondary" onClick={onClose}>
                    {label('wms.common.close')}
                </Button>
            </div>
        </div>
    ) : null;

    const form = (
        <form onSubmit={handleSubmit} className="space-y-5">
            {/* O caminho NORMAL, dito antes do formulário e com a porta aberta:
                quem queria a jornada automática sai daqui num clique, em vez de
                descobrir no fim que montou à mão o que o planejador faria. */}
            <section className="space-y-2 rounded-lg border border-mono-200 bg-mono-50 p-3 dark:border-mono-800 dark:bg-mono-900/40">
                {newHelp ? <p className="text-xs leading-snug text-mono-600 dark:text-mono-400">{newHelp}</p> : null}
                <p className="text-xs leading-snug text-mono-600 dark:text-mono-400">
                    {label('wms.allocation.description')}
                </p>
                <Button variant="secondary" size="sm" disabled={saving} onClick={() => onUseAllocation?.()}>
                    <Package size={14} className="mr-1.5" />
                    {label('wms.allocation.title')}
                </Button>
            </section>

            <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                    <Select
                        label={label('wms.service_order.field.type')}
                        value={typeCode}
                        disabled={saving || types.length === 0}
                        errorMessage={typeMissing ? label('wms.common.required') : undefined}
                        onChange={(event) => {
                            setTypeCode(event.target.value);
                            setTypeMissing(false);
                        }}
                    >
                        <option value="">—</option>
                        {types.map((type) => (
                            <option key={type.id ?? type.code} value={type.code}>
                                {type.label || type.code}
                            </option>
                        ))}
                    </Select>
                    {/* O COMPORTAMENTO é do cadastro, não desta tela: mostrá-lo
                        aqui evita criar à mão uma OS de reabastecimento achando
                        que é uma movimentação qualquer. */}
                    {selectedType?.behavior_key ? (
                        <Badge variant="info">{label(`wms.behavior.${selectedType.behavior_key}`)}</Badge>
                    ) : null}
                    {types.length === 0 && noTypesHelp ? (
                        <p className="text-xs text-warning-700 dark:text-warning-400">{noTypesHelp}</p>
                    ) : null}
                </div>

                <div className="space-y-1">
                    <Select
                        label={label('wms.field.status')}
                        value={status}
                        disabled={saving}
                        onChange={(event) => setStatus(event.target.value)}
                    >
                        {SERVICE_ORDER_CREATE_STATUSES.map((option) => (
                            <option key={option} value={option}>
                                {label(`wms.service_order.status.${option}`)}
                            </option>
                        ))}
                    </Select>
                    {/* A CONSEQUÊNCIA de nascer em cada coluna, com as palavras
                        que o quadro já usa — o Supervisor está escolhendo se o
                        chão pode pegar esta OS agora. */}
                    <p className="text-[11px] leading-snug text-mono-500 dark:text-mono-400">
                        {label(`wms.kanban.${status}_help`)}
                    </p>
                </div>
            </div>

            <section className="space-y-2">
                <h3 className="text-sm font-semibold text-black dark:text-white">
                    {label('wms.service_order.field.items')}
                </h3>
                {itemsHelp ? <p className="text-xs leading-snug text-mono-600 dark:text-mono-400">{itemsHelp}</p> : null}

                {rows.map((row, index) => {
                    const invalid = invalidRowKeys.includes(row.key);
                    const from = locationByCode.get(String(row.from_location_code || '').toUpperCase());
                    const to = locationByCode.get(String(row.to_location_code || '').toUpperCase());
                    return (
                        <div key={row.key} className="flex flex-wrap items-start gap-2">
                            {/* A ORDEM das linhas é a ordem de execução (o
                                backend deriva a `seq` daqui): o número não é
                                enfeite, é o que o operador vai ver no coletor. */}
                            <div className="mt-8 w-5 shrink-0">
                                <Id value={index + 1} className="text-xs text-mono-400" />
                            </div>
                            <div className="min-w-[160px] flex-1">
                                <Input
                                    label={label('wms.service_order_item.from')}
                                    value={row.from_location_code}
                                    list={locationListId}
                                    autoComplete="off"
                                    disabled={saving}
                                    className={MONO_CLASS}
                                    errorMessage={
                                        invalid && !row.from_location_code.trim()
                                            ? label('wms.common.required')
                                            : undefined
                                    }
                                    onChange={(event) =>
                                        changeRow(row.key, { from_location_code: event.target.value.toUpperCase() })
                                    }
                                />
                                {from ? (
                                    <p className="mt-1 truncate text-[11px] text-mono-500 dark:text-mono-400">
                                        {FormatLocationPlain(from, { label })}
                                    </p>
                                ) : null}
                            </div>
                            <div className="min-w-[160px] flex-1">
                                <Input
                                    label={label('wms.service_order_item.to')}
                                    value={row.to_location_code}
                                    list={locationListId}
                                    autoComplete="off"
                                    disabled={saving}
                                    className={MONO_CLASS}
                                    errorMessage={
                                        invalid && !row.to_location_code.trim()
                                            ? label('wms.common.required')
                                            : undefined
                                    }
                                    onChange={(event) =>
                                        changeRow(row.key, { to_location_code: event.target.value.toUpperCase() })
                                    }
                                />
                                {to ? (
                                    <p className="mt-1 truncate text-[11px] text-mono-500 dark:text-mono-400">
                                        {FormatLocationPlain(to, { label })}
                                    </p>
                                ) : null}
                            </div>
                            {/* LPN OPCIONAL: sem ele o item é "o que estiver
                                nesta posição", e quem cobra a realidade é o bipe
                                da execução — não o planejamento. */}
                            <div className="min-w-[140px] flex-1">
                                <Input
                                    label={label('wms.collector.lpn_label')}
                                    value={row.lpn_code}
                                    autoComplete="off"
                                    disabled={saving}
                                    className={MONO_CLASS}
                                    onChange={(event) =>
                                        changeRow(row.key, { lpn_code: event.target.value.toUpperCase() })
                                    }
                                />
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="mt-6"
                                disabled={saving || rows.length <= 1}
                                onClick={() => removeRow(row.key)}
                                aria-label={label('wms.lpn_create.remove_item')}
                            >
                                <Trash size={16} />
                            </Button>
                        </div>
                    );
                })}

                <datalist id={locationListId}>
                    {locations.rows.map((location) => (
                        <option key={location.id ?? location.code} value={location.code}>
                            {FormatLocationPlain(location, { label })}
                        </option>
                    ))}
                </datalist>

                <Button variant="secondary" size="sm" disabled={saving} onClick={addRow}>
                    <Plus size={14} className="mr-1.5" />
                    {label('wms.lpn_create.add_item')}
                </Button>
            </section>

            <div className="grid gap-4 sm:grid-cols-2">
                <div>
                    <Input
                        label={label('wms.service_order.field.equipment')}
                        value={equipmentCode}
                        list={equipmentListId}
                        autoComplete="off"
                        disabled={saving}
                        className={MONO_CLASS}
                        onChange={(event) => setEquipmentCode(event.target.value.toUpperCase())}
                    />
                    <datalist id={equipmentListId}>
                        {equipmentRows.map((equipment) => (
                            <option key={equipment.id ?? equipment.code} value={equipment.code}>
                                {equipment.type ? label(`wms.equipment_type.${equipment.type}`) : ''}
                            </option>
                        ))}
                    </datalist>
                </div>
                <Textarea
                    label={label('wms.field.notes')}
                    rows={3}
                    maxLength={2000}
                    value={notes}
                    disabled={saving}
                    onChange={(event) => setNotes(event.target.value)}
                />
            </div>

            {submitError ? (
                <p className="rounded-lg border border-danger-300 bg-danger-soft p-3 text-sm text-danger-800 dark:border-danger-800 dark:bg-danger-900/20 dark:text-danger-300">
                    {submitError}
                </p>
            ) : null}

            <div className="flex flex-wrap gap-3 pt-1">
                {/* Sem tipo ATIVO cadastrado não há OS para criar: aí sim o
                    botão fica fora do ar — não há o que o Supervisor corrija
                    nesta tela. */}
                <Button type="submit" variant="primary" loading={saving} disabled={types.length === 0}>
                    <Check size={16} className="mr-1.5" />
                    {label('wms.service_orders.new_submit')}
                </Button>
                <Button type="button" variant="secondary" disabled={saving} onClick={onClose}>
                    {label('wms.common.cancel')}
                </Button>
            </div>
        </form>
    );

    return (
        <Modal isOpen onClose={saving ? undefined : onClose} size="4xl" title={label('wms.service_orders.new')}>
            {created ? receipt : form}
        </Modal>
    );
};

export default ServiceOrderCreateModal;
